import { Injectable } from '@angular/core';
import { KnowledgeSource } from '@vendasta/embeddings';

@Injectable({
  providedIn: 'root',
})
export class FileTypeIconService {
  private readonly mimetypeIconMap = new Map<string, string>([
    // CSV
    ['text/csv', 'table_chart'],
    ['application/csv', 'table_chart'],

    // DOCX
    ['application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'description'],

    // HTML
    ['text/html', 'code'],
    ['application/xhtml', 'code'],

    // Images
    ['image/jpeg', 'image'],
    ['image/png', 'image'],

    // PDF
    ['application/pdf', 'picture_as_pdf'],
    ['application/x-pdf', 'picture_as_pdf'],

    // Text files (including JSON and Markdown)
    ['application/json', 'code'],
    ['application/markdown', 'description'],

    // PPTX
    ['application/vnd.openxmlformats-officedocument.presentationml', 'slideshow'],

    // XLS
    ['application/vnd.ms-excel', 'table_chart'],
    ['application/excel', 'table_chart'],

    // XLSX
    ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'table_chart'],
  ]);

  /**
   * Gets the Material Design icon name for a given mimetype
   * @param mimetype The file mimetype
   * @returns The Material Design icon name, defaults to 'description' for unknown types
   */
  getIconForMimetype(mimetype: string | null | undefined): string {
    if (!mimetype) {
      return 'description';
    }

    // Check exact match first
    const exactMatch = this.mimetypeIconMap.get(mimetype.toLowerCase());
    if (exactMatch) {
      return exactMatch;
    }

    // Check for text/* prefix (which covers .txt, .text, .md, .markdown, .json, .jsonl)
    const lowerMimetype = mimetype.toLowerCase();
    if (lowerMimetype.startsWith('text/')) {
      // Most text files should use description icon
      if (lowerMimetype === 'text/csv') {
        return 'table_chart'; // Already handled above, but being explicit
      } else if (lowerMimetype === 'text/html') {
        return 'code'; // Already handled above, but being explicit
      }
      return 'description';
    }

    // Default fallback for unsupported types
    return 'description';
  }

  /**
   * Gets the icon name for a knowledge source based on its configuration
   * @param source The knowledge source
   * @returns The appropriate Material Design icon name
   */
  getKnowledgeSourceIcon(source: KnowledgeSource): string {
    const mimetype = source?.config?.fileConfig?.mimetype ||
      source?.config?.fileConfig?.queuedFile?.mimetype ||
      source?.config?.fileConfig?.erroredFile?.mimetype;
      
    if (mimetype) {
      return this.getIconForMimetype(mimetype);
    }

    if (source?.config?.websiteScrapeConfig) {
      return 'language';
    }

    if (source?.config?.customDataConfig) {
      return 'edit_note';
    }

    if (source?.config?.businessProfileConfig) {
      return 'business';
    }

    // Default knowledge icon fallback
    return 'auto_stories';
  }
}
